import { GisLayoutOrientation } from '../types/interchange'

interface GeoJSONFeature {
  id: string
  type: string
  geometry: {
    type: string
    coordinates: number[][] | number[]
  }
  properties: {
    name: string
    type: string
    status?: string
    state?: string
    lengthFeet?: number
    facingTrackId?: string
    normalTrackId?: string
    reverseTrackId?: string
  }
}

interface BoundingBox {
  minLon: number
  maxLon: number
  minLat: number
  maxLat: number
}

export interface MapViewState {
  centerLon: number
  centerLat: number
  zoomScale: number
}

export interface CanvasOffset {
  x: number
  y: number
}

export const calculateBoundingBox = (features: GeoJSONFeature[]): BoundingBox => {
  let minLon = Infinity
  let maxLon = -Infinity
  let minLat = Infinity
  let maxLat = -Infinity

  features.forEach((feature) => {
    if (feature.geometry.type === 'LineString') {
      const coords = feature.geometry.coordinates as number[][]
      coords.forEach(([lon, lat]) => {
        minLon = Math.min(minLon, lon)
        maxLon = Math.max(maxLon, lon)
        minLat = Math.min(minLat, lat)
        maxLat = Math.max(maxLat, lat)
      })
    } else if (feature.geometry.type === 'Point') {
      const [lon, lat] = feature.geometry.coordinates as number[]
      minLon = Math.min(minLon, lon)
      maxLon = Math.max(maxLon, lon)
      minLat = Math.min(minLat, lat)
      maxLat = Math.max(maxLat, lat)
    }
  })

  return { minLon, maxLon, minLat, maxLat }
}

export const initializeCameraState = (
  bbox: BoundingBox,
  width: number,
  height: number,
  padding = 120
): MapViewState => {
  const lonRange = bbox.maxLon - bbox.minLon
  const latRange = bbox.maxLat - bbox.minLat

  const availableWidth = width - padding * 2
  const availableHeight = height - padding * 2

  const scaleX = availableWidth / lonRange
  const scaleY = availableHeight / latRange
  let zoomScale = Math.min(scaleX, scaleY)

  // Don't clamp initial zoomScale - let it fit the bounding box naturally
  // The calculated value is correct for initial view fitting

  const centerLon = (bbox.minLon + bbox.maxLon) / 2
  const centerLat = (bbox.minLat + bbox.maxLat) / 2

  return { centerLon, centerLat, zoomScale }
}

export const projectToCanvas = (
  lon: number,
  lat: number,
  cameraState: MapViewState,
  canvasOffset: CanvasOffset,
  width: number,
  height: number
): { x: number; y: number } => {
  const { centerLon, centerLat, zoomScale } = cameraState

  // Project coordinates relative to camera center
  const x = width / 2 + canvasOffset.x + (lon - centerLon) * zoomScale
  const y = height / 2 + canvasOffset.y + (centerLat - lat) * zoomScale

  return { x, y }
}

export const canvasToCoordinates = (
  x: number,
  y: number,
  cameraState: MapViewState,
  canvasOffset: CanvasOffset,
  width: number,
  height: number
): { lon: number; lat: number } => {
  const { centerLon, centerLat, zoomScale } = cameraState

  // Reverse the projection
  const lon = centerLon + (x - width / 2 - canvasOffset.x) / zoomScale
  const lat = centerLat - (y - height / 2 - canvasOffset.y) / zoomScale

  return { lon, lat }
}

// Legacy function kept for backward compatibility during transition
export const projectToCanvasLegacy = (
  lon: number,
  lat: number,
  bbox: BoundingBox,
  width: number,
  height: number,
  padding = 120
): { x: number; y: number } => {
  const lonRange = bbox.maxLon - bbox.minLon
  const latRange = bbox.maxLat - bbox.minLat

  const availableWidth = width - padding * 2
  const availableHeight = height - padding * 2

  const scaleX = availableWidth / lonRange
  const scaleY = availableHeight / latRange
  const scale = Math.min(scaleX, scaleY)

  // Center the content within the available space
  const contentWidth = lonRange * scale
  const contentHeight = latRange * scale
  const offsetX = (availableWidth - contentWidth) / 2
  const offsetY = (availableHeight - contentHeight) / 2

  const x = padding + offsetX + (lon - bbox.minLon) * scale
  const y = padding + offsetY + (bbox.maxLat - lat) * scale

  return { x, y }
}

export const applyOrientationTransform = (
  x: number,
  y: number,
  orientation: GisLayoutOrientation,
  canvasWidth: number
): { x: number; y: number } => {
  switch (orientation) {
    case 'REVERSED':
      return { x: canvasWidth - x, y }
    case 'NORMAL':
    default:
      return { x, y }
  }
}

export const interpolateTrackPosition = (
  track: GeoJSONFeature,
  linearOffset: number,
  cameraState: MapViewState,
  canvasOffset: CanvasOffset,
  stageWidth: number,
  stageHeight: number,
  activeOrientation: GisLayoutOrientation
): { x: number; y: number; angle: number } => {
  const coords = track.geometry.coordinates as number[][]
  
  if (coords.length < 2) {
    const projected = projectToCanvas(coords[0][0], coords[0][1], cameraState, canvasOffset, stageWidth, stageHeight)
    const transformed = applyOrientationTransform(projected.x, projected.y, activeOrientation, stageWidth)
    return { x: transformed.x, y: transformed.y, angle: 0 }
  }
  
  // Get track length in feet from properties, with fallback
  const trackLengthFeet = track.properties?.lengthFeet || 2000
  
  // Normalize linear offset to percentage (0.0 to 1.0) with strict clamp
  // This prevents any bounce/reverse behavior at track boundaries
  const targetRatio = Math.min(Math.max(linearOffset / trackLengthFeet, 0), 1)
  
  // Calculate actual segment distances in coordinate degrees
  const segmentLengths: number[] = []
  let totalTrackLengthDegrees = 0
  
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i]
    const [lon2, lat2] = coords[i + 1]
    const segmentLength = Math.sqrt(
      Math.pow(lon2 - lon1, 2) + Math.pow(lat2 - lat1, 2)
    )
    segmentLengths.push(segmentLength)
    totalTrackLengthDegrees += segmentLength
  }
  
  // Map target ratio to target distance in degrees
  const targetDistanceDegrees = targetRatio * totalTrackLengthDegrees
  
  // Find the segment containing the target distance
  let accumulatedDistance = 0
  for (let i = 0; i < segmentLengths.length; i++) {
    if (accumulatedDistance + segmentLengths[i] >= targetDistanceDegrees) {
      // Interpolate within this segment
      const segmentProgress = (targetDistanceDegrees - accumulatedDistance) / segmentLengths[i]
      const [lon1, lat1] = coords[i]
      const [lon2, lat2] = coords[i + 1]
      const interpLon = lon1 + (lon2 - lon1) * segmentProgress
      const interpLat = lat1 + (lat2 - lat1) * segmentProgress
      
      const projected = projectToCanvas(interpLon, interpLat, cameraState, canvasOffset, stageWidth, stageHeight)
      const transformed = applyOrientationTransform(projected.x, projected.y, activeOrientation, stageWidth)
      
      // Calculate angle with canvas Y-axis inversion fix
      const deltaX = lon2 - lon1
      const deltaY = lat2 - lat1
      const angleRad = Math.atan2(-deltaY, deltaX)
      const angleDeg = angleRad * (180 / Math.PI)
      
      return { x: transformed.x, y: transformed.y, angle: angleDeg }
    }
    accumulatedDistance += segmentLengths[i]
  }
  
  // Fallback: return last point if offset is beyond track length
  const [lastLon, lastLat] = coords[coords.length - 1]
  const projected = projectToCanvas(lastLon, lastLat, cameraState, canvasOffset, stageWidth, stageHeight)
  const transformed = applyOrientationTransform(projected.x, projected.y, activeOrientation, stageWidth)
  const [prevLon, prevLat] = coords[coords.length - 2]
  const deltaX = lastLon - prevLon
  const deltaY = lastLat - prevLat
  const angleRad = Math.atan2(-deltaY, deltaX)
  const angleDeg = angleRad * (180 / Math.PI)
  
  return { x: transformed.x, y: transformed.y, angle: angleDeg }
}

export const calculateClearancePoint = (
  track: GeoJSONFeature,
  switchFeature: GeoJSONFeature,
  clearanceOffsetFeet: number = 75,
  cameraState: MapViewState,
  canvasOffset: CanvasOffset,
  stageWidth: number,
  stageHeight: number,
  yardOrientation: GisLayoutOrientation
): { x: number; y: number; angle: number; splitIndex: number } | null => {
  const coords = track.geometry.coordinates as number[][]
  
  if (coords.length < 2) {
    return null
  }
  
  // Get track length in feet from properties, with fallback
  const trackLengthFeet = track.properties?.lengthFeet || 2000
  
  // Determine if this is the normal or reverse track
  const isNormalTrack = switchFeature.properties.normalTrackId === track.id
  const isReverseTrack = switchFeature.properties.reverseTrackId === track.id
  
  if (!isNormalTrack && !isReverseTrack) {
    return null
  }
  
  // For normal track, clearance is measured from the switch (start of track)
  // For reverse track, clearance is measured from the switch (end of track)
  // We'll use the same interpolation logic but need to handle direction
  
  // Calculate segment distances in coordinate degrees
  const segmentLengths: number[] = []
  let totalTrackLengthDegrees = 0
  
  for (let i = 0; i < coords.length - 1; i++) {
    const [lon1, lat1] = coords[i]
    const [lon2, lat2] = coords[i + 1]
    const segmentLength = Math.sqrt(
      Math.pow(lon2 - lon1, 2) + Math.pow(lat2 - lat1, 2)
    )
    segmentLengths.push(segmentLength)
    totalTrackLengthDegrees += segmentLength
  }
  
  // Normalize clearance offset to percentage (0.0 to 1.0)
  const clearanceRatio = Math.max(0, Math.min(clearanceOffsetFeet / trackLengthFeet, 1.0))
  
  // Map clearance ratio to target distance in degrees
  const targetDistanceDegrees = clearanceRatio * totalTrackLengthDegrees
  
  // Find the segment containing the clearance point
  let accumulatedDistance = 0
  for (let i = 0; i < segmentLengths.length; i++) {
    if (accumulatedDistance + segmentLengths[i] >= targetDistanceDegrees) {
      // Interpolate within this segment
      const segmentProgress = (targetDistanceDegrees - accumulatedDistance) / segmentLengths[i]
      const [lon1, lat1] = coords[i]
      const [lon2, lat2] = coords[i + 1]
      const interpLon = lon1 + (lon2 - lon1) * segmentProgress
      const interpLat = lat1 + (lat2 - lat1) * segmentProgress
      
      const projected = projectToCanvas(interpLon, interpLat, cameraState, canvasOffset, stageWidth, stageHeight)
      const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageWidth)
      
      // Calculate angle with canvas Y-axis inversion fix
      const deltaX = lon2 - lon1
      const deltaY = lat2 - lat1
      const angleRad = Math.atan2(-deltaY, deltaX)
      const angleDeg = angleRad * (180 / Math.PI)
      
      // Return the split index (the coordinate index where clearance point falls)
      // We split at i+1 to include the clearance point in the safe berth segment
      return { x: transformed.x, y: transformed.y, angle: angleDeg, splitIndex: i + 1 }
    }
    accumulatedDistance += segmentLengths[i]
  }
  
  // Fallback: return last point if offset is beyond track length
  const [lastLon, lastLat] = coords[coords.length - 1]
  const projected = projectToCanvas(lastLon, lastLat, cameraState, canvasOffset, stageWidth, stageHeight)
  const transformed = applyOrientationTransform(projected.x, projected.y, yardOrientation, stageWidth)
  const [prevLon, prevLat] = coords[coords.length - 2]
  const deltaX = lastLon - prevLon
  const deltaY = lastLat - prevLat
  const angleRad = Math.atan2(-deltaY, deltaX)
  const angleDeg = angleRad * (180 / Math.PI)
  
  return { x: transformed.x, y: transformed.y, angle: angleDeg, splitIndex: coords.length - 1 }
}
